import {
  Client,
  createRemoteClient,
  GlobalCGEventTracker,
  makeEcosystemMixedOpStore,
  StaticOpStore,
  Weave,
} from '@wandb/weave/core';
import React, {useEffect, useMemo, useState} from 'react';

import {Spec as PanelEach} from './components/Panel2/PanelEach';
import {Spec as PanelEachColumn} from './components/Panel2/PanelEachColumn';
import {Spec as PanelExpr} from './components/Panel2/PanelExpr';
import {Spec as PanelFacet} from './components/Panel2/PanelFacet';
// import {Spec as PanelFacetTabs} from './components/Panel2/PanelFacetTabs';
import {Spec as PanelGroup} from './components/Panel2/PanelGroup';
import {Spec as PanelSelectEditor} from './components/Panel2/PanelSelectEditor';
// import {Spec as PanelSections} from './components/Panel2/PanelSections';
import {Spec as PanelSlider} from './components/Panel2/PanelSlider';
import {
  RowSize,
  TABLE_CONFIG_DEFAULTS,
} from './components/Panel2/PanelTable/config';
import {useLoadWeaveObjects} from './components/Panel2/weaveBackend';
import getConfig from './config';
import {ClientContext, WeaveFeatures, WeaveFeaturesContext} from './context';

let GLOBAL_CLIENT: Client | null = null;

export const getGlobalWeaveContext = () => {
  if (GLOBAL_CLIENT == null) {
    throw new Error('Global Weave Context not initialized');
  }
  return new Weave(GLOBAL_CLIENT);
};

/**
 * Pass-through provider that bridges a Client prop into ClientContext (weave/context.tsx).
 * This sits high in the App tree — see the cascade documented on ClientContext in context.tsx.
 *
 * Caveat: this component holds an `isLoading` state that subscribes to the client's
 * loadingObservable, so the component itself re-renders whenever the compute graph is
 * executing. However, context consumers are NOT affected because:
 *   - The context `value` is memoized via `useMemo(() => ({client}), [client])` — the
 *     reference stays stable across isLoading toggles, so React Context does not notify
 *     consumers (https://react.dev/reference/react/useContext#usage).
 *   - `children` is a prop from the React.memo'd parent (ComputeGraphContextProvider),
 *     so the element reference is stable and React bails out of reconciling the subtree.
 *   - Only the wrapper `<div>` className toggles (CSS loading indicator).
 *
 * If the context value shape grows or starts carrying frequently-changing state, consider
 * migrating to a zustand store (https://zustand.docs.pmnd.rs/getting-started/comparison).
 */
export const ComputeGraphContextProviderFromClient: React.FC<{
  children?: React.ReactNode;
  client: Client;
}> = React.memo(({client, children}) => {
  const context = useMemo(() => ({client}), [client]);
  GLOBAL_CLIENT = client;

  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const subscription = context.client
      .loadingObservable()
      .subscribe(setIsLoading);
    return () => subscription.unsubscribe();
  }, [context]);

  return (
    <div
      data-test="compute-graph-provider"
      data-test-num-shadow-server-requests-counter={
        GlobalCGEventTracker.shadowServerRequests
      }
      className={isLoading ? 'loading cg-executing' : ''}
      style={{height: '100%'}}>
      <ClientContext.Provider value={context}>
        {children}
      </ClientContext.Provider>
    </div>
  );
});
ComputeGraphContextProviderFromClient.displayName =
  'ComputeGraphContextProviderFromClient';

const useRemoteEcosystemClient = (
  isAdmin: boolean = false,
  tokenFunc: () => Promise<string | undefined>
) => {
  const {loading, remoteOpStore} = useLoadWeaveObjects();
  return useMemo(() => {
    if (loading || remoteOpStore == null) {
      return null;
    }
    return createRemoteClient(
      getConfig().backendWeaveExecutionUrl(),
      tokenFunc,
      isAdmin,
      makeEcosystemMixedOpStore(StaticOpStore.getInstance(), remoteOpStore),
      (input, init) => window.fetch(input as any, init)
    );
  }, [isAdmin, remoteOpStore, tokenFunc, loading]);
};

export const RemoteEcosystemComputeGraphContextProvider: React.FC<{
  children?: React.ReactNode;
  isAdmin?: boolean;
  tokenFunc?: () => Promise<string | undefined>;
}> = React.memo(({isAdmin, tokenFunc, children}) => {
  const tf = useMemo(() => {
    const dummy = async () => undefined;
    return tokenFunc != null ? tokenFunc : dummy;
  }, [tokenFunc]);
  const client = useRemoteEcosystemClient(!!isAdmin, tf);
  if (client == null) {
    return <></>;
  }
  return (
    <ComputeGraphContextProviderFromClient client={client}>
      {children}
    </ComputeGraphContextProviderFromClient>
  );
});
RemoteEcosystemComputeGraphContextProvider.displayName =
  'RemoteEcosystemComputeGraphContextProvider';

const UNHIDE_PANELS = [
  PanelGroup,
  PanelEachColumn,
  PanelExpr,
  PanelFacet,
  // PanelFacetTabs,
  // PanelSections,
  PanelEach,
  PanelSlider,
  PanelSelectEditor,
];

export const NotebookComputeGraphContextProvider: React.FC<{
  children?: React.ReactNode;
}> = React.memo(({children}) => {
  // Default state when in jupyter notebook weave session
  const defaultWBFeatureState: WeaveFeatures = useMemo(
    () => ({
      actions: true,
      fullscreenMode: true,
      clientEvalInUseNodeValueEnabled: true,
      sidebarConfigStylingEnabled: true,
      errorBoundaryInPanelComp2Enabled: true,
      redesignedPlotConfigEnabled: true,
      betaFeatures: {
        'weave-python-ecosystem': true,
        'weave-devpopup': false,
      },
    }),
    []
  );

  TABLE_CONFIG_DEFAULTS.rowSize = RowSize.Small;
  for (const panel of UNHIDE_PANELS) {
    panel.hidden = false;
  }

  return (
    <WeaveFeaturesContext.Provider value={defaultWBFeatureState}>
      <RemoteEcosystemComputeGraphContextProvider>
        {children}
      </RemoteEcosystemComputeGraphContextProvider>
    </WeaveFeaturesContext.Provider>
  );
});
NotebookComputeGraphContextProvider.displayName =
  'NotebookComputeGraphContextProvider';
